'use client';

import * as React from 'react';
import { clsx } from 'clsx';
import { CandidateCardAvatar } from './CandidateCardAvatar';
import styles from './CandidateCardProfessional.module.css';

export interface CandidateCardProfessionalSkill {
  id: number | string;
  name: string;
  type?: string;
  logo?: string;
  emoji?: string;
}

export interface CandidateCardProfessionalProps extends React.HTMLAttributes<HTMLDivElement> {
  candidate: {
    id: number | string;
    name: string;
    position_title?: string;
    highlights?: string;
    avatar?: string;
    blur?: boolean;
    skills?: CandidateCardProfessionalSkill[];
  };
  recruiterLogoUrl?: string;
  recruiterName?: string;
  className?: string;
}

const MAX_SKILL_LENGTH = 50;
const MIN_SKILL_NUMBER = 5;

export const CandidateCardProfessional = React.forwardRef<
  HTMLDivElement,
  CandidateCardProfessionalProps
>(({ candidate, recruiterLogoUrl, recruiterName, className, ...props }, ref) => {
  const skills: CandidateCardProfessionalSkill[] = [];
  let characterCount = 0;
  for (const skill of candidate.skills || []) {
    if (
      characterCount + skill.name.length > MAX_SKILL_LENGTH &&
      skills.length >= MIN_SKILL_NUMBER
    ) {
      break;
    }
    skills.push(skill);
    characterCount += skill.name.length;
  }
  const additionalSkills = (candidate.skills?.length ?? 0) - skills.length;

  return (
    <div
      ref={ref}
      id={`card-${candidate.id}`}
      className={clsx(styles.root, className)}
      {...props}
    >
      <div className={styles.card}>
        <div className={styles.top}>
          <div className={styles.avatarWrapper}>
            <CandidateCardAvatar
              id={`card-avatar-${candidate.id}`}
              name={candidate.name}
              src={candidate.avatar}
              blur={candidate.blur}
              size={150}
              className={styles.avatarImage}
              blurClassName={styles.avatarBlurred}
            />
          </div>

          <div className={styles.headings}>
            <h2 className={styles.name}>{candidate.name}</h2>
            <h4 className={styles.title}>{candidate.position_title}</h4>
            <p className={styles.highlights}>{candidate.highlights}</p>
          </div>

          {recruiterLogoUrl && (
            <div className={styles.recruiterLogo}>
              <img src={recruiterLogoUrl} alt={recruiterName || ''} />
            </div>
          )}
        </div>

        <div className={styles.attributes}>
          <div className={styles.hiddenAttribute} />
          {skills.map((item) => (
            <span className={styles.attribute} key={`${item.type}-${item.id}`}>
              {item.logo && (
                <img
                  src={item.logo}
                  alt={item.name}
                  title={item.name}
                  className={styles.skillLogo}
                />
              )}
              {item.emoji && <span className={styles.emoji}>{item.emoji}</span>}
              <span
                className={styles.skillName}
                style={{ marginLeft: item.logo || item.emoji ? '8px' : '4px' }}
              >
                {item.name}
              </span>
            </span>
          ))}
          {additionalSkills > 0 && (
            <span className={styles.moreAttributes}>+{additionalSkills}</span>
          )}
          <span className={styles.viewMore}>Click for more</span>
        </div>
      </div>
    </div>
  );
});

CandidateCardProfessional.displayName = 'CandidateCardProfessional';

export default CandidateCardProfessional;
